<template>
    <Dialog v-model:visible="visible" modal header="Quick Grade Setup"
        class="w-[95vw] max-w-3xl"
        :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
        <div class="mb-4 text-color-secondary">
            Select a template to quickly generate a grading system. This will add the grades to your existing list.
        </div>

        <div class="grid grid-cols-1 gap-4" :class="{ 'md:grid-cols-2': !isTertiary }">
            <!-- Tertiary Template -->
            <div v-if="isTertiary"
                class="border rounded-md p-4 flex flex-col h-full bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                <h3 class="text-xl font-bold mb-2">Undergraduate</h3>
                <p class="text-sm text-gray-500 mb-4">Standard 5-point GPA system (A–F)</p>

                <div class="flex-grow space-y-2 mb-4">
                    <div v-for="grade in tertiaryTemplatePreview" :key="grade.grade as string"
                        class="flex justify-between gap-2 text-sm p-2 bg-white dark:bg-slate-800 rounded border border-slate-100 dark:border-slate-700">
                        <span class="font-bold w-8">{{ grade.grade }}</span>
                        <span class="text-gray-600 dark:text-gray-300 flex-grow">{{ grade.name }}</span>
                        <span class="text-gray-500 font-mono">{{ grade.minimum }} - {{ grade.maximum }}</span>
                        <span class="text-primary font-mono w-10 text-right">{{ Number(grade.point).toFixed(1) }}</span>
                    </div>
                </div>

                <Button size="small" label="Use Undergraduate Template" icon="pi pi-check" severity="primary" outlined
                    class="w-full mt-auto" @click="selectTertiary" data-testid="btn-grade-quick-tertiary" />
            </div>

            <!-- Junior / Primary Template -->
            <div v-if="!isTertiary"
                class="border rounded-md p-4 flex flex-col h-full bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                <h3 class="text-xl font-bold mb-2">Junior School / Primary</h3>
                <p class="text-sm text-gray-500 mb-4">Standard 5-point grading system (A-E)</p>

                <div class="flex-grow space-y-2 mb-4">
                    <div v-for="grade in juniorTemplatePreview" :key="grade.grade as string"
                        class="flex justify-between text-sm p-2 bg-white dark:bg-slate-800 rounded border border-slate-100 dark:border-slate-700">
                        <span class="font-bold w-12">{{ grade.grade }}</span>
                        <span class="text-gray-600 dark:text-gray-300 flex-grow">{{ grade.name }}</span>
                        <span class="text-gray-500 font-mono">{{ grade.minimum }} - {{ grade.maximum }}</span>
                    </div>
                </div>

                <Button size="small" label="Use Junior Template" icon="pi pi-check" severity="primary" outlined
                    class="w-full mt-auto" @click="selectJunior" data-testid="btn-grade-quick-junior" />
            </div>

            <!-- Senior Secondary Template -->
            <div v-if="!isTertiary"
                class="border rounded-md p-4 flex flex-col h-full bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700">
                <h3 class="text-xl font-bold mb-2">Senior School</h3>
                <p class="text-sm text-gray-500 mb-4">WAEC/NECO Standard 9-point system (A1-F9)</p>

                <div class="flex-grow space-y-2 mb-4">
                    <div v-for="grade in seniorTemplatePreview" :key="grade.grade as string"
                        class="flex justify-between text-sm p-2 bg-white dark:bg-slate-800 rounded border border-slate-100 dark:border-slate-700">
                        <span class="font-bold w-12">{{ grade.grade }}</span>
                        <span class="text-gray-600 dark:text-gray-300 flex-grow">{{ grade.name }}</span>
                        <span class="text-gray-500 font-mono">{{ grade.minimum }} - {{ grade.maximum }}</span>
                    </div>
                </div>

                <Button size="small" label="Use Senior Template" icon="pi pi-check" severity="primary" outlined
                    class="w-full mt-auto" @click="selectSenior" data-testid="btn-grade-quick-senior" />
            </div>
        </div>
    </Dialog>
</template>

<script setup lang="ts">
import type { SchoolGradeInput } from '@/types/composition-functions';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';

const { isTertiary } = useSchoolFeatures();

const emit = defineEmits(['update:visible', 'create-batch']);
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
});

const visible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val),
});

const juniorTemplatePreview: SchoolGradeInput[] = [
  { grade: 'A', name: 'Excellent', minimum: 70, maximum: 100, group: 'Junior School' },
  { grade: 'B', name: 'Very Good', minimum: 60, maximum: 69, group: 'Junior School' },
  { grade: 'C', name: 'Good', minimum: 50, maximum: 59, group: 'Junior School' },
  { grade: 'D', name: 'Pass', minimum: 45, maximum: 49, group: 'Junior School' },
  { grade: 'E', name: 'Fail', minimum: 0, maximum: 44, group: 'Junior School' },
];

const seniorTemplatePreview: SchoolGradeInput[] = [
  { grade: 'A1', name: 'Distinction', minimum: 75, maximum: 100, group: 'Senior School' },
  { grade: 'B2', name: 'Very Good', minimum: 70, maximum: 74, group: 'Senior School' },
  { grade: 'B3', name: 'Good', minimum: 65, maximum: 69, group: 'Senior School' },
  { grade: 'C4', name: 'Credit', minimum: 60, maximum: 64, group: 'Senior School' },
  { grade: 'C5', name: 'Credit', minimum: 55, maximum: 59, group: 'Senior School' },
  { grade: 'C6', name: 'Credit', minimum: 50, maximum: 54, group: 'Senior School' },
  { grade: 'D7', name: 'Pass', minimum: 45, maximum: 49, group: 'Senior School' },
  { grade: 'E8', name: 'Pass', minimum: 40, maximum: 44, group: 'Senior School' },
  { grade: 'F9', name: 'Fail', minimum: 0, maximum: 39, group: 'Senior School' },
];

const tertiaryTemplatePreview: SchoolGradeInput[] = [
  { grade: 'A', name: 'Excellent', minimum: 70, maximum: 100, point: 5.0, group: 'Undergraduate' },
  { grade: 'B', name: 'Very Good', minimum: 60, maximum: 69, point: 4.0, group: 'Undergraduate' },
  { grade: 'C', name: 'Good', minimum: 50, maximum: 59, point: 3.0, group: 'Undergraduate' },
  { grade: 'D', name: 'Average', minimum: 45, maximum: 49, point: 2.0, group: 'Undergraduate' },
  { grade: 'E', name: 'Pass', minimum: 40, maximum: 44, point: 1.0, group: 'Undergraduate' },
  { grade: 'F', name: 'Fail', minimum: 0, maximum: 39, point: 0.0, group: 'Undergraduate' },
];

const selectJunior = () => {
  emit('create-batch', juniorTemplatePreview);
  visible.value = false;
};

const selectSenior = () => {
  emit('create-batch', seniorTemplatePreview);
  visible.value = false;
};

const selectTertiary = () => {
  emit('create-batch', tertiaryTemplatePreview);
  visible.value = false;
};
</script>
